import { QUEZONE_SYSTEM_PROMPT, VARIABLE_SYSTEM_PROMPT } from "./bookingPrompt";
import { bookingTools } from "./bookingTools";

const MAX_TOOL_ROUNDS = 6;

const normalizeAssistantMessage = (data) => {
  const message = data?.choices?.[0]?.message;
  if (message) return message;

  return {
    role: "assistant",
    content: data?.content?.[0]?.text || data?.message || "",
  };
};

const getTextContent = (message) => {
  if (typeof message?.content === "string") return message.content;
  if (Array.isArray(message?.content)) {
    return message.content.map((part) => part?.text || "").join("");
  }
  return "";
};

const parseToolArguments = (rawArgs) => {
  if (!rawArgs) return {};
  if (typeof rawArgs === "object") return rawArgs;
  try {
    return JSON.parse(rawArgs);
  } catch (error) {
    return {};
  }
};

const normalizeToolCalls = (toolCalls = []) => (
  toolCalls.map((toolCall) => ({
    ...toolCall,
    type: toolCall.type || "function",
    function: toolCall.function || {
      name: toolCall.name,
      arguments: typeof toolCall.arguments === "string"
        ? toolCall.arguments
        : JSON.stringify(toolCall.arguments || {}),
    },
  }))
);

const toConversationMessage = (message) => {
  const conversationMessage = {
    role: message.role,
    content: getTextContent(message) || message.content || null,
  };

  const toolCalls = message.tool_calls || message.toolCalls;
  if (toolCalls?.length) {
    conversationMessage.tool_calls = normalizeToolCalls(toolCalls);
  }

  if (message.tool_call_id) {
    conversationMessage.tool_call_id = message.tool_call_id;
  }

  if (message.name) {
    conversationMessage.name = message.name;
  }

  return conversationMessage;
};

export default class BookingAgent {
  constructor({ aiService, toolExecutor, systemPrompt }) {
    this.aiService = aiService;
    this.toolExecutor = toolExecutor;
    this.systemPrompt = systemPrompt;
  }

  async respond({ messages }) {
    const conversationMessages = messages.map(toConversationMessage);
    const conversation = [
      { role: "system", content: this.systemPrompt },
      { role: "system", content: VARIABLE_SYSTEM_PROMPT },
      ...conversationMessages,
    ];

    let lastAssistantMessage = null;

    for (let round = 0; round < MAX_TOOL_ROUNDS; round += 1) {
      const data = await this.aiService.createChatCompletion({
        messages: conversation,
        tools: bookingTools,
      });
      const assistantMessage = normalizeAssistantMessage(data);
      const toolCalls = assistantMessage.tool_calls || assistantMessage.toolCalls || [];

      lastAssistantMessage = assistantMessage;

      if (!toolCalls.length) {
        const finalAssistantMessage = toConversationMessage(assistantMessage);
        conversation.push(finalAssistantMessage);

        return {
          content: getTextContent(assistantMessage) || "I'm having trouble responding right now. Please try again.",
          messages: conversation.slice(1),
        };
      }

      conversation.push({
        role: "assistant",
        content: getTextContent(assistantMessage) || null,
        tool_calls: normalizeToolCalls(toolCalls),
      });

      for (const toolCall of toolCalls) {
        const name = toolCall.function?.name || toolCall.name;
        const args = parseToolArguments(toolCall.function?.arguments || toolCall.arguments);

        try {
          const result = await this.toolExecutor.execute(name, args);

          if (result?.status === "ADDITIONAL_INFO_REQUIRED") {
            const assistantMessage = {
              role: "assistant",
              content: "Additional information is required to complete your booking. A form will open now so you can provide it, then I will retry your booking automatically.",
            };
            conversation.push(assistantMessage);

            return {
              content: assistantMessage.content,
              messages: conversation.slice(1),
            };
          }

          conversation.push({
            role: "tool",
            tool_call_id: toolCall.id,
            name,
            content: JSON.stringify(result),
          });
        } catch (error) {
          conversation.push({
            role: "tool",
            tool_call_id: toolCall.id,
            name,
            content: JSON.stringify({
              error: error.message || "Tool execution failed.",
            }),
          });
        }
      }
    }

    return {
      content: getTextContent(lastAssistantMessage) || "I need a moment to complete that. Please try again.",
      messages: conversation.slice(1),
    };
  }
}
